/**
 * Pure geometry helpers shared by the ceiling-edit client
 * (`web-app/src/utils/ceilingEdit.js`) and the cloud partition override
 * (`functions/features/ceiling-region-partition.js`). Keeping the
 * divider→XZ resolution and the region representative-point math in one
 * place is the DRY requirement called out in the ceiling-editing plan
 * (claude-docs/2026-06-27-ceiling-editing.md §3a).
 *
 * A divider vertex is a tagged union (plan decision #8):
 *   { onWall: { wallId, s } }  s = metres along the wall from
 *                              alignment.bottomLeft (same convention as
 *                              pointOnWallAtS / the `partial:` locks).
 *   { free:   { x, z } }       interior bend, absolute XZ.
 *
 * All geometry is in the top-down XZ plane (y dropped). Increasing z is
 * screen-down (see CLAUDE.md); none of the helpers here depend on the
 * sign convention, they only need consistency.
 */

const { isPointInsidePolygon } = require('../geometry/pointInPolygon');

/**
 * Point on a wall's interior face at along-wall position `s` (metres
 * from alignment.bottomLeft), clamped to the wall span. Returns null
 * when the alignment is missing or degenerate. Mirrors
 * `pointOnWallAtS` in functions/features/ceiling-generation.js — kept
 * here as the pure, dependency-free copy so web-app can call it too.
 */
function pointOnWallAtS(s, alignment) {
  const bl = alignment?.bottomLeft;
  const br = alignment?.bottomRight;
  if (!bl || !br) return null;
  const dx = br.x - bl.x;
  const dz = br.z - bl.z;
  const L = Math.hypot(dx, dz);
  if (L < 1e-9) return null;
  const t = Math.max(0, Math.min(L, s)) / L;
  return { x: bl.x + t * dx, z: bl.z + t * dz };
}

/**
 * Along-wall coordinate (metres from alignment.bottomLeft toward
 * bottomRight) of an XZ point projected onto the wall line, clamped to
 * the wall span. The inverse of pointOnWallAtS. Returns null for a
 * degenerate wall.
 */
function alongWallS(xz, alignment) {
  const bl = alignment?.bottomLeft;
  const br = alignment?.bottomRight;
  if (!bl || !br || !xz) return null;
  const dx = br.x - bl.x;
  const dz = br.z - bl.z;
  const L = Math.hypot(dx, dz);
  if (L < 1e-9) return null;
  const s = ((xz.x - bl.x) * dx + (xz.z - bl.z) * dz) / L;
  return Math.max(0, Math.min(L, s));
}

/**
 * Perpendicular distance from an XZ point to a wall's interior-face line
 * (finite segment bl→br). Used to pick the nearest wall when snapping a
 * drawn divider endpoint.
 */
function distToWall(xz, alignment) {
  const s = alongWallS(xz, alignment);
  if (s == null) return Infinity;
  const foot = pointOnWallAtS(s, alignment);
  return Math.hypot(xz.x - foot.x, xz.z - foot.z);
}

/**
 * Snap a drawn XZ point to the nearest room wall, returning the divider
 * vertex `{ onWall: { wallId, s } }` plus the snap distance, or null if
 * no wall is within `maxDist` metres (the caller then stores it as a
 * `{ free }` interior bend). Walls need `id` + `alignment`.
 */
function snapPointToWall(xz, walls, maxDist = 0.4) {
  let best = null;
  for (const w of walls || []) {
    if (!w?.id || !w.alignment) continue;
    const d = distToWall(xz, w.alignment);
    if (d <= maxDist && (!best || d < best.dist)) {
      best = { wallId: w.id, s: alongWallS(xz, w.alignment), dist: d };
    }
  }
  if (!best) return null;
  return { onWall: { wallId: best.wallId, s: best.s }, dist: best.dist };
}

/**
 * Length of a wall's interior face (BL→BR distance) in XZ.
 */
function wallLen(alignment) {
  const bl = alignment?.bottomLeft;
  const br = alignment?.bottomRight;
  if (!bl || !br) return 0;
  return Math.hypot(br.x - bl.x, br.z - bl.z);
}

/**
 * Like `snapPointToWall`, but then nudges the along-wall position to the
 * nearest "notable" spot on that wall — a corner (s = 0 or s = L) or the
 * midpoint (s = L/2) — when the click lands within `snapTolM` of one. This
 * makes an approximate click that's clearly aiming at the middle (to split
 * a room in half) or an end land exactly there, instead of a few cm off.
 * Returns `{ onWall: { wallId, s }, xz, snappedTo }` (xz = the resolved
 * point for display), or null when no wall is within `maxDist`.
 *
 * Editor-only helper — the base `snapPointToWall` stays raw so seed/derived
 * geometry isn't pulled to midpoints.
 */
function snapPointToWallNotable(
  xz,
  walls,
  { maxDist = 0.4, snapTolM = 0.35 } = {}
) {
  const base = snapPointToWall(xz, walls, maxDist);
  if (!base) return null;
  const wall = (walls || []).find((w) => w.id === base.onWall.wallId);
  const align = wall?.alignment;
  const L = wallLen(align);
  let s = base.onWall.s;
  let snappedTo = null;
  if (L > 0) {
    const targets = [
      { s: 0, kind: 'corner' },
      { s: L / 2, kind: 'midpoint' },
      { s: L, kind: 'corner' },
    ];
    let bestD = snapTolM;
    for (const t of targets) {
      const d = Math.abs(s - t.s);
      if (d < bestD) {
        bestD = d;
        s = t.s;
        snappedTo = t.kind;
      }
    }
  }
  return {
    onWall: { wallId: base.onWall.wallId, s },
    xz: pointOnWallAtS(s, align) || xz,
    snappedTo,
  };
}

/**
 * Resolve one divider vertex to an absolute XZ point.
 * @param {Object} vertex - { onWall:{wallId,s} } | { free:{x,z} }
 * @param {Map<string,Object>} wallsById - room-wall id → wall (needs .alignment)
 * @returns {{x:number,z:number}|null} null when an onWall vertex references
 *   a missing/degenerate wall (caller drops the divider + warns, plan §7).
 */
function resolveDividerVertex(vertex, wallsById) {
  if (!vertex) return null;
  if (vertex.free) {
    const { x, z } = vertex.free;
    if (typeof x === 'number' && typeof z === 'number') return { x, z };
    return null;
  }
  if (vertex.onWall) {
    const wall = wallsById.get(vertex.onWall.wallId);
    if (!wall) return null;
    return pointOnWallAtS(vertex.onWall.s, wall.alignment);
  }
  return null;
}

/**
 * Resolve every vertex of a divider to XZ. Returns null if ANY vertex
 * fails to resolve (a partially-resolvable divider is dropped whole —
 * we never silently truncate a polyline, plan §2).
 * @returns {Array<{x,z}>|null}
 */
function resolveDividerVertices(divider, wallsById) {
  const verts = divider?.vertices;
  if (!Array.isArray(verts) || verts.length < 2) return null;
  const out = [];
  for (const v of verts) {
    const p = resolveDividerVertex(v, wallsById);
    if (!p) return null;
    out.push(p);
  }
  return out;
}

/**
 * The XZ segments of a divider polyline: N vertices → N-1 segments
 * (open polyline; closed/island dividers are deferred, decision #6).
 * @returns {Array<{start:{x,z}, end:{x,z}}>}
 */
function dividerSegmentsXZ(divider, wallsById) {
  const pts = resolveDividerVertices(divider, wallsById);
  if (!pts) return [];
  const segs = [];
  for (let i = 0; i < pts.length - 1; i++) {
    segs.push({ start: pts[i], end: pts[i + 1] });
  }
  return segs;
}

/**
 * Ray-cast point-in-polygon in XZ (even-odd rule). Boundary points may
 * return either result — callers must not rely on edge behaviour.
 */
function pointInPolygonXZ(pt, polygonXZ) {
  if (!pt || !Array.isArray(polygonXZ) || polygonXZ.length < 3) return false;
  return isPointInsidePolygon(pt, polygonXZ);
}

/**
 * A guaranteed-interior representative point of a simple XZ polygon —
 * NOT the centroid, whose value for a non-convex (L-shaped) cell can
 * fall outside the polygon and break point-in-polygon re-resolution on
 * the first regen with zero user edits (plan §2, decision revised
 * 2026-06-30).
 *
 * Algorithm: a pole of inaccessibility — the interior point FARTHEST from
 * any edge (like Mapbox polylabel, but self-contained, no dependency). A
 * coarse-to-fine grid search maximizes the distance-to-nearest-edge over
 * interior candidates, so on an L-shape the marker sits well inside the
 * widest limb instead of near a wall (which the old widest-horizontal-span
 * heuristic could do). Falls back to a scan-line span midpoint, then the
 * centroid, for degenerate input.
 */
function representativeInteriorPointXZ(polygonXZ) {
  if (!Array.isArray(polygonXZ) || polygonXZ.length < 3) return null;
  const pole = poleOfInaccessibilityXZ(polygonXZ);
  if (pole) return pole;
  return scanLineInteriorPointXZ(polygonXZ) || centroidXZ(polygonXZ);
}

/** Distance from XZ point to segment [a,b]. */
function distToSegmentXZ(p, a, b) {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len2 = dx * dx + dz * dz;
  let t = len2 > 0 ? ((p.x - a.x) * dx + (p.z - a.z) * dz) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  const cx = a.x + t * dx;
  const cz = a.z + t * dz;
  return Math.hypot(p.x - cx, p.z - cz);
}

/** Min distance from an interior point to any polygon edge. */
function minDistToEdgesXZ(p, poly) {
  let min = Infinity;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const d = distToSegmentXZ(p, poly[j], poly[i]);
    if (d < min) min = d;
  }
  return min;
}

/**
 * Interior point maximizing distance to the nearest edge, via a
 * coarse-to-fine grid search. Returns null if no interior sample is found
 * (extremely thin polygon) so the caller can fall back.
 */
function poleOfInaccessibilityXZ(polygonXZ, rounds = 4, gridN = 12) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of polygonXZ) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
  }
  if (!(maxX > minX) || !(maxZ > minZ)) return null;

  let best = null;
  let bestD = -Infinity;
  let x0 = minX;
  let x1 = maxX;
  let z0 = minZ;
  let z1 = maxZ;
  for (let r = 0; r < rounds; r++) {
    const stepX = (x1 - x0) / gridN;
    const stepZ = (z1 - z0) / gridN;
    for (let i = 0; i <= gridN; i++) {
      for (let j = 0; j <= gridN; j++) {
        const cand = { x: x0 + i * stepX, z: z0 + j * stepZ };
        if (!pointInPolygonXZ(cand, polygonXZ)) continue;
        const d = minDistToEdgesXZ(cand, polygonXZ);
        if (d > bestD) {
          bestD = d;
          best = cand;
        }
      }
    }
    if (!best) return null;
    // Refine: search a window around the best point at finer resolution.
    x0 = best.x - stepX;
    x1 = best.x + stepX;
    z0 = best.z - stepZ;
    z1 = best.z + stepZ;
  }
  return best;
}

/** Fallback: midpoint of the widest horizontal interior span. */
function scanLineInteriorPointXZ(polygonXZ, scanLines = 7) {
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of polygonXZ) {
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
  }
  if (!(maxZ > minZ)) return null;
  let best = null;
  let bestWidth = -Infinity;
  const n = polygonXZ.length;
  for (let s = 1; s <= scanLines; s++) {
    const z = minZ + ((maxZ - minZ) * s) / (scanLines + 1);
    const xs = [];
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const zi = polygonXZ[i].z;
      const zj = polygonXZ[j].z;
      if (zi > z !== zj > z) {
        const xi = polygonXZ[i].x;
        const xj = polygonXZ[j].x;
        xs.push(xi + ((xj - xi) * (z - zi)) / (zj - zi));
      }
    }
    xs.sort((a, b) => a - b);
    for (let k = 0; k + 1 < xs.length; k += 2) {
      const width = xs[k + 1] - xs[k];
      if (width > bestWidth) {
        bestWidth = width;
        best = { x: (xs[k] + xs[k + 1]) / 2, z };
      }
    }
  }
  return best;
}

function centroidXZ(polygonXZ) {
  let x = 0;
  let z = 0;
  for (const p of polygonXZ) {
    x += p.x;
    z += p.z;
  }
  return { x: x / polygonXZ.length, z: z / polygonXZ.length };
}

/** Build a room-wall id → wall map from a room. */
function wallsByIdFromRoom(room) {
  const map = new Map();
  for (const w of room?.walls || []) {
    if (w?.id) map.set(w.id, w);
  }
  return map;
}

module.exports = {
  pointOnWallAtS,
  alongWallS,
  distToWall,
  snapPointToWall,
  snapPointToWallNotable,
  resolveDividerVertex,
  resolveDividerVertices,
  dividerSegmentsXZ,
  pointInPolygonXZ,
  representativeInteriorPointXZ,
  centroidXZ,
  wallsByIdFromRoom,
};
